import { type Tokens } from '@chakra-ui/react/styled-system';
import { type TextProps } from '@chakra-ui/react/text';
import { isPlainObject } from 'es-toolkit';

// Based on https://github.com/toss/es-toolkit/blob/main/src/object/flattenObject.ts
export const flattenTokens = (object: object, prefix: string = ''): Record<string, any> => {
  const result: Record<string, any> = {};
  const keys = Object.keys(object);

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const value = (object as any)[key];

    const prefixedKey = prefix ? `${prefix}/${key}` : key;

    if (isPlainObject(value) && Object.keys(value).length > 0 && Object.keys(value)[0] !== 'value') {
      Object.assign(result, flattenTokens(value, prefixedKey));
      continue;
    }

    result[prefixedKey] = value;
  }

  return result;
};

export function withSlashes(object: Record<string, any>): Record<string, any> {
  return {
    ...object,
    ...flattenTokens(object),
  };
}

/**
 *
 * @param input Value in pixels
 * @returns Value in rems, typed precisely enough to satisfy props that take a `${number}rem`
 */
export const rem = (input: number): `${number}rem` => {
  return `${input / 16}rem`;
};

/**
 * The colour tokens under the given prefixes, for props that must take a semantic token.
 *
 * `Tokens` is generated in the consumer's own project by `chakra typegen`, which
 * `scripts/postinstall.cjs` runs and which exits 0 with a warning when it cannot. Where it did not
 * run, `Tokens['colors']` is Chakra's stock union, the `Extract` has nothing to match and collapses
 * to `never` -- which would make every prop using it a type error pointing at the wrong thing. So it
 * falls back to `string`: the narrowing is a guard where the types are real, not a tripwire where
 * they are not.
 */
export type BitkitColorToken<Prefix extends string> = [Extract<Tokens['colors'], `${Prefix}/${string}`>] extends [never]
  ? string
  : Extract<Tokens['colors'], `${Prefix}/${string}`>;

export const getTextStyle = (as: TextProps['as']) => {
  if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(as as string)) {
    return `heading/${as}`;
  }
  return undefined;
};
